import React,{useMemo} from 'react';
import { Analyzation, Clinics, Patients,Affiliation, SpecializedMedicalListEnum } from '@sleepwell_new_platform/common-types';
import { AverageItem } from '@sleepwell_new_platform/common-types/src/averageItems';
import {  styled } from '@mui/system';
import {  Typography } from '@mui/material';
import { Spacer } from '../../../atoms/Spacer';
import {PatientInfoCard} from "../../../molecules/report/PatientInfoCardPDF"
import { Headline, ResultHeadline } from '../../../molecules/HeadlinePDF';
import { HeadlessTableForPDF, MultiColmunsTableForPDF } from '../../../molecules/TableForPDF';
import { ResultTable } from '../../../molecules/report/ResultTablePDF';
import SleepRaderChartA4ForPDF from '../../../molecules/chart/SleepRaderChartA4ForPDF';
import { convertGlobalToLocalPhoneNumber, convertToYYYYMMDD, roundToFive } from '../../../../utils/commons';
import { totalJudgeResultList } from '../../../../static/report';
import { minToHHMM } from '../../../../utils/timeFormatter';
import { roundToSecondDecimalPlace } from '../../../../utils/analyzations/roundFunctions';
import { MedicalPartnersTypeEnum } from '@sleepwell_new_platform/common-functions/lib/admins';
import { specialistTypes } from '../../../../static/medicalPartner';
import { prefectureMapList } from '../../../../static/prefecture';
import SleepLineChart from '../../../molecules/chart/SleepLineChartPDF';
import { DeltaPowerTimeChart } from '../../../molecules/chart/DeltaPowerTimeChartPDF';
import { GoodSleepCondition } from '../../../molecules/sleepReport/GoodSleepConditionPDF';

interface ResearchReportPDFProps {
  reportNumber: number;
  analyzationData?: any;
  pageNumber: number;
}

type Props = {
  analyzationId: string;
  openReportModal?: boolean;
  age?: number;
  measureStartDate?: number;
  clinic?: Clinics | null;
  lang?: 'ja' | 'en';
  type?: "analysis" | "reAnalysis" | "reCalculate";
  isPdfDownload?: boolean;
  edfFileName?: string;
  patients?: Patients[];
  patientId?: string;
  isImmediateAnalyize?: number;
  anaylzation?: Analyzation;
  analyzationResultData?: any;
  averageItems?: AverageItem[];
  snoreQuestion?: number;
  analyzation?: Analyzation | undefined,
  affiliation?: Affiliation | null;
  // 新しく追加するprops
  customerMedicalPartnerDisabled?: any[];
  medicalPartners?: any[];
}


export const DoctorA4ReportPDF: React.FC<ResearchReportPDFProps & Props> = ({ 
  reportNumber, 
  analyzationData,
  pageNumber,
  analyzationId,
  openReportModal,
  age,
  measureStartDate,
  clinic,
  lang,
  type,
  isPdfDownload,
  edfFileName,
  patients,
  patientId,
  isImmediateAnalyize,
  anaylzation,
  analyzationResultData,
  affiliation,
  analyzation,
  snoreQuestion,
  averageItems,
  customerMedicalPartnerDisabled,
  medicalPartners
}) => {
  // 親コンポーネントから渡された解析結果データを安全に処理
  const resultData = analyzationResultData || null;

  const tableHeader2 = useMemo(() => {
    if(lang === 'ja'){
      return ['', '解釈', <>同世代・<br/>同世代の<br/>平均</>, <>あなたの<br/>睡眠</>, '所見'];
    } else {
      return ['', 'Interpretation', 'Average sleep of the same generation, same sex ', 'Your sleep', 'Findings'];
    }
  },[lang]);

  const tableHeader3 = useMemo(() => {
    if(lang === 'ja'){
      return  ['', '病院名', '電話番号', '住所'];
    } else {
      return ['', 'Clinc Name', 'Phone Number', 'Address'];
    }},[lang]);

      const tableRow2 = useMemo(() => {
    const tibScoreComment = resultData?.tibScoreComment;
      let rows: {value: string, align: "left" | "center" | "right" | "justify" | "inherit" | undefined }[][];
      if(lang === 'ja'){
        rows = [
          [
            { value: '総就床時間', align: 'left' },
            { value: '7時間を中心に、短すぎても長すぎても健康に影響があるとされています', align: 'left' },
            { value: `${String(minToHHMM(resultData?.tibAverage) ?? '')}`, align: 'right' },
            { value: `${String(minToHHMM(resultData?.tib) ?? 'ー')}`, align: 'right' },
            { value: tibScoreComment?.commentJP ?? "", align: 'left' }
          ],
          [
            { value: '寝付き', align: 'left' },
            { value: '30分以上かかっていると要注意。毎日、1時間以上かかるようなら専門医に要相談', align: 'left' },
            { value: `${String(minToHHMM(resultData?.fallSleepAverage) ?? '')}`, align: 'right' },
            { value: `${minToHHMM(resultData?.sl) ?? 'ー'}`, align: 'right' },
            { value: resultData?.fallSleepComment?.commentJP ?? "", align: 'left' }
          ],
          [
            { value: '熟眠度', align: 'left' },
            { value: '深い睡眠の時に出現する脳波(デルタ波)の量です。健康であっても年齢とともに減少します。詳しくは熟眠脳波年齢の欄を参照下さい。', align: 'left' },
            { value: `${resultData?.sleepQualityAverage ?? 'ー'}μV./分`, align: 'right' },
            { value: `${resultData?.firstNonremDeltaPowerInfo.totalDeltaPowParMin ?? 'ー'}μV./分`, align: 'right' },
            { value: resultData?.sleepQualityComment?.commentJP ?? "", align: 'left' }
          ],
          [
            { value: '中途覚醒', align: 'left' },
            { value: '10％未満なら問題ありません。20％を超えている場合、毎日同じような状況でしたら専門医にご相談下さい。', align: 'left' },
            { value: `${resultData?.wStageTotalMinAverage}％`, align: 'right' },
            { value: `${resultData?.wStageRatio ?? 'ー'}％`, align: 'right' },
            { value: resultData?.wStageTotalMinComment?.commentJP ?? "", align: 'left' }
          ],
          [
            { value: '睡眠効率', align: 'left' },
            { value: '85％以上あれば問題ありません。70％以下は要注意。', align: 'left' },
            { value: `${resultData?.seTibAverage}％`, align: 'right' },
            { value: `${resultData?.seTib ?? 'ー'}％`, align: 'right' },
            { value: resultData?.seTibComment?.commentJP ?? "", align: 'left' }
          ],
          [
            { value: '睡眠リズム', align: 'left' },
            { value: '60〜120分サイクルで、ノンレム睡眠とレム睡眠が繰り返されます。精神的に疲れていると最初のリズムが短くなったりすることもあります。', align: 'left' },
            { value: String(resultData?.sleepRismAverage), align: 'right' },
            { value: resultData?.sleepRism === 0 ?  '0' : resultData?.sleepRism  ? String(roundToSecondDecimalPlace(resultData?.sleepRism)) : '-', align: 'right' },
            { value: resultData?.sleepRismComment?.commentJP ?? "", align: 'left' }
          ]
        ];
      } else {
        rows = [
          [
            { value: 'Time in Bed (TIB)', align: 'left' },
            { value: 'The average length of sleep is 7 hours. Having too much or too little sleep is said to affect your health.', align: 'left' },
            { value: `${String(minToHHMM(resultData?.tibAverage, "en") ?? '')}`, align: 'right' },
            { value: `${String(minToHHMM(resultData?.tib, "en") ?? '')}`, align: 'right' },
            { value: tibScoreComment?.commentEN ?? "", align: 'left' }
          ],
          [
            { value: 'Sleep Latency', align: 'left' },
            { value: 'You should take note if it takes longer than 30 minutes. If it takes longer than an hour every day, consult a specialist.', align: 'left' },
            { value: `${String(minToHHMM(resultData?.fallSleepAverage, "en") ?? '')}`, align: 'right' },
            { value: `${minToHHMM(resultData?.sl, "en") ?? 'ー'}`, align: 'right' },
            { value: resultData?.fallSleepComment?.commentEN ?? "", align: 'left' }
          ],
          [
            { value: 'Depth of Sleep', align: 'left' },
            { value: 'This is the volume of brainwaves (delta waves) that occur during deep sleep. This decreases as you age. Please refer to the deep sleep brainwave section for details.', align: 'left' },
            { value: `${resultData?.sleepQualityAverage ?? 'ー'}μV./min`, align: 'right' },
            { value: `${resultData?.firstNonremDeltaPowerInfo.totalDeltaPowParMin}μV./min`, align: 'right' },
            { value: resultData?.sleepQualityComment?.commentEN ?? "", align: 'left' }
          ],
          [
            { value: 'Time Awake During Sleep', align: 'left' },
            { value: 'Moving includes waking, rolling over, grinding teeth, etc. If the percentage exceeds 20% on a daily basis, you should consult a specialist.', align: 'left' },
            { value: `${resultData?.wStageTotalMinAverage}％`, align: 'right' },
            { value: `${resultData?.wStageRatio ?? 'ー'}％`, align: 'right' },
            { value: resultData?.wStageTotalMinComment?.commentEN ?? "", align: 'left' }
          ],
          [
            { value: 'Sleep Efficiency', align: 'left' },
            { value: '85% or more is not problematic. 70% or less needs attention.', align: 'left' },
            { value: `${resultData?.seTibAverage}％`, align: 'right' },
            { value: `${resultData?.seTib ?? 'ー'}％`, align: 'right' },
            { value: resultData?.seTibComment?.commentEN ?? "", align: 'left' }
          ],
          [
            { value: 'Rhythm', align: 'left' },
            { value: 'Periods of non-REM sleep and REM sleep are repeated in 60 to 120 minute cycles. If you are mentally tired the first rhythm may shorten.', align: 'left' },
            { value: String(analyzationResultData?.sleepRismAverage), align: 'right' },
            { value: analyzationResultData?.sleepRism === 0 ?  '0' : analyzationResultData?.sleepRism  ? String(roundToSecondDecimalPlace(analyzationResultData?.sleepRism)) : '-', align: 'right' },
            { value: analyzationResultData?.sleepRismComment?.commentEN ?? "", align: 'left' }
          ]
        ];
      }
      if(age && age < 19){
        // rowsの最後の配列を削除するす
        rows.pop();
      }
      return rows;
    },[analyzationResultData, lang, age]);

  const tableRows = useMemo(() => {
    let rows: Array<{title: string, value: JSX.Element | React.ReactNode}> =[]
    if(lang === 'ja'){
      rows =  [
        {title: '睡眠脳波', value: analyzationResultData?.totalJudgeInfo.totalCommentJP},
        {title: 'いびき質問票', value: analyzationResultData?.snoreAnswer?.snoreAnswerJP?.replace("<br/>", '\n')},
        {title: 'アテネ不眠尺度', value: analyzation?.athens},
      ]
    } else {
      rows = [
        {title: 'Sleep EEG', value: analyzationResultData?.totalJudgeInfo.totalCommentEN},
        {title: 'Snore Questionnaire', value: analyzationResultData?.snoreAnswer?.snoreAnswerEN?.replace("<br/>", '\n')},
        {title: 'Athens Insomnia Scale', value: analyzation?.athens},
      ]
    }
    const isEnableSnoreQuestion = analyzation?.clinic?.isEnableSnoreQuestion;
    if(!isEnableSnoreQuestion || analyzation.snoreQuestion === 0){
      rows = rows.filter((row) => !(row.title === 'いびき質問票' || row.title === 'Snore Questionnaire'))
    }
    if(!analyzation?.clinic?.isEnableAthens || analyzation?.athens === 0){
      rows = rows.filter((row) => !(row.title === 'アテネ不眠尺度' || row.title === 'Athens Insomnia Scale'))
    }
    return rows;
  },[lang, analyzationResultData, analyzation]);

  const tableRow3 = useMemo(() => {
    const totalJudge = resultData?.totalJudgeInfo.totalJudge ?? "";
    // 都道府県
    
    const isInputPatientAddress = clinic?.isInputPatientAddress;
    // ランクに応じたレポートの可否
    const specializedMedicalList: string[] = clinic?.specializedMedicalList ?? [];
    const medicalPartnerDisabledIds = customerMedicalPartnerDisabled?.map((medicalPartner) => medicalPartner.medicalPartnerId) ?? [];
    const currentPatientData = patients?.find((patient) => patient.id === patientId);
    const sortedMedicalPartners = medicalPartners?.filter((medicalPartner) => {
      return medicalPartner.prefecture === currentPatientData?.prefecture && !medicalPartnerDisabledIds.includes(medicalPartner.id);
    })?.sort((a, b) => (a.index && b.index) && a?.index > b?.index ? 1 : -1);
    const isBlankMedicalPartners = (isInputPatientAddress === "prefecture" && specializedMedicalList?.includes((totalJudge ?? "").toLowerCase())) ? false : true;
   const list:  {value: string, align: "center" | "inherit" | "left" | "right" | "justify" | undefined}[][]= [];
      const colums = [MedicalPartnersTypeEnum.GENERAL_SLEEP_SPECIALIST, MedicalPartnersTypeEnum.SLEEP_APNOEA_SYNDROME, MedicalPartnersTypeEnum.MENTAL_SPECIALIST];
      colums.forEach((column, index) => {
        // ここで英語日本語の対応を
        // ja or en or both
        const medicalPartner = isBlankMedicalPartners ? [] : sortedMedicalPartners?.filter((medicalPartner) => medicalPartner.type === column  && [lang, "both"].includes(medicalPartner.reportLang));
        if(!medicalPartner?.length) {
          if(lang === "ja"){
            list.push([
              { value: `${index+1}.${column}`, align: 'center' },
              { value: "", align: 'center' },
              { value: "", align: 'center' },
              { value: "", align: 'center' }
            ]);
          } else {
            list.push([
              { value: `${index+1}.${specialistTypes.find((doc)=>doc.ja === column)?.en}`, align: 'center' },
              { value: "", align: 'center' },
              { value: "", align: 'center' },
              { value: "", align: 'center' }
            ]);
          }
        } else {
          const maxLength = medicalPartner?.length > 2 ? 2 : medicalPartner?.length;
          for(let i = 0; i < maxLength; i++) {
            const medicalPartnerData = medicalPartner[i];
            if(lang === "ja"){
              list.push([
                { value: `${index+1}.${column}`, align: 'center' },
                { value: medicalPartnerData.clinicName, align: 'center' },
                { value: medicalPartnerData?.phoneNumber ?? "", align: 'center' },
                { value: medicalPartnerData.address, align: 'center' }
              ]);
            } else {
              list.push([
                { value: `${index+1}.${specialistTypes.find((doc)=>doc.ja === column)?.en}`, align: 'center' },
                { value: medicalPartnerData.clinicNameEN, align: 'center' },
                { value: medicalPartnerData?.phoneNumberEN ?? "", align: 'center' },
                { value: medicalPartnerData.addressEN, align: 'center' }
              ]);
            }
          }
        }
      });
    const filteredList = list.filter((doc) => doc[1].value !== "");
    return filteredList;
  },[resultData, medicalPartners, lang, customerMedicalPartnerDisabled, clinic, analyzation, patients, patientId]);

  const sleepEvaluation = useMemo(() => {
    const data = [
      {
        subject: lang === 'ja' ? '総就寝時間' : 'Time in Bed(TIB)',
        you: roundToFive(resultData?.tibScore ?? 0),
        average: 3,
      },
      {
        subject: lang === 'ja' ?  '寝つき' : 'Sleep Latency',
        you: roundToFive(resultData?.fallSleepScore ?? 0),
        average: 3,
      }, 
      {
        subject: lang === 'ja' ? '熟眠度' : 'Depth of Sleep',
        you: roundToFive(resultData?.sleepQualityScore ?? 0),
        average: 3,
      },
      {
        subject: lang === 'ja' ?'中途覚醒' : 'Time Awake During Sleep',
        you:  roundToFive(resultData?.wStageTotalMinScore ?? 0),
        average: 3,
      },
      {
        subject: lang === 'ja' ? '睡眠効率' : 'Sleep Efficiency',
        you:  roundToFive(resultData?.seTibScore ?? 0),
        average: 3,
      },
      {
        subject: lang === 'ja' ? '睡眠リズム' : 'Rhythm',
        you: resultData?.sleepRism ? roundToFive(resultData?.sleepRismScore ?? 0) : 3,
        average: 3,
      },
    ];
    if(age && age < 19){
      return data.filter((doc) => !(doc.subject === '睡眠リズム' || doc.subject === 'Rhythm' ));
    }
    return data;
  },[resultData, lang]);

  const patientData = useMemo(() => {
    const patientData = patients?.find((patient) => patient.id === patientId);
    return patientData
  },[patients, patientId]);

  const reportMark = useMemo(() => {
    const _isImmediateAnalyize = isImmediateAnalyize;
    const isReCalc  = type === "reCalculate" ? true : false;
    if(isReCalc){
      return "R"
    }
    if(!_isImmediateAnalyize){
      return "*"
    }
    return ""
  }, [isImmediateAnalyize, type]);

  /***************************************************
   * ページ1
   ***************************************************/
  const renderPage1 = () => (
    <Container>
      <div style={{marginTop: 20}}>
        <PatientInfoCard
          gender={patientData?.gender ?? ''}
          patientName={patientData?.name ?? ''}
          patientNumber={patientData?.patientNumber ?? 0}
          birthdayMillis={convertToYYYYMMDD(patientData?.birthday??"")}
          measureStartDate={measureStartDate??0}
          age={age??0}
          affiliationName={affiliation?.name ?? ""}
          lang={lang}
        />
         <Spacer height={12} />
        </div>
      <ResultHeadline 
        rank={resultData?.totalJudgeInfo.totalJudge ?? ""} 
        result={lang === 'ja' ? totalJudgeResultList.find((doc)=>doc.rank === resultData?.totalJudgeInfo.totalJudge)?.labelJP ?? "" : totalJudgeResultList.find((doc)=>doc.rank === resultData?.totalJudgeInfo.totalJudge)?.labelEN ?? ""} 
        lang={lang??'ja'}/>
         <Spacer height={8} />
      <HeadlessTableForPDF rows={tableRows} />
      <Spacer height={8} />
      <Headline
        title={lang === 'ja' ? "SleepScope 脳波検査結果" : "SleepScope Result of Sleep Inspection"}
      />
      <Spacer height={8} />
      <ResultTable
      lang={lang}
      headers={tableHeader2}
      rows={tableRow2}
      />
              <SleepRaderChartA4ForPDF
          sleepEvaluation={sleepEvaluation}
          averageItems={averageItems??[]}
          patientData={patientData}
          matureSleepBrainwaveAge={resultData?.matureSleepBrainwaveAge ?? 0}
          lang={lang??'ja'}
          age={age??0}
          totalDeltaPowParMin={resultData?.firstNonremDeltaPowerInfo.totalDeltaPowParMin ?? 0}
        />
      {(() => {
        const displayCondition = clinic?.specializedMedicalList?.includes(resultData?.totalJudgeInfo?.totalJudge?.toLowerCase() as SpecializedMedicalListEnum);
        return displayCondition;
      })() && <>
      <Headline
        title={lang === 'ja' ? "睡眠専門医" :"Sleep specialist"}
      />
      <Spacer height={8} />
       <MultiColmunsTableForPDF
        headers={tableHeader3}
        rows={tableRow3}
      />
      </>}
       <div style={{marginTop: 12}}>
      {lang === 'ja' ? <Typography style={{fontSize: 15, color: "#898989"}}>{`${reportMark}${clinic?.nameJP ?? ""}　電話番号: ${clinic?.phoneNumberJP ? convertGlobalToLocalPhoneNumber(clinic?.phoneNumberJP) : ""}　住所: ${clinic?.prefecture ? clinic?.prefecture : ""}${clinic?.address ?? ""}`}</Typography> :
       <Typography style={{fontSize: 15, color: "#898989"}}>{`${reportMark}${clinic?.nameEN ?? ""}　phoneNumber: ${clinic?.phoneNumberEN ? clinic?.phoneNumberEN : ""}　address: ${clinic?.addressEn ?? ""} ${clinic?.prefecture ? prefectureMapList.find((doc) => doc.label === clinic?.prefecture)?.enLabel  : ""}`}</Typography>}
      </div>
    </Container>
  );

    /***************************************************
   * ページ2
   ***************************************************/
  const renderPage2 = () => (
      <Container>
        <Spacer height={12} />
        <Headline
            title={lang === 'ja' ?"睡眠経過図：睡眠時間における睡眠段階の経過推移" : "Hypnogram:Transitions in Sleep Stages"}
          />
          <Spacer height={8} />
                  <SleepLineChart
          analyzationResultData={resultData}
          openReportModal={openReportModal ?? false}
          measureStartDate={measureStartDate ?? 0}
          lang={lang ?? 'ja'}
          height={310}
          isScale={true}
        />
        <Headline
          title={lang === 'ja' ?"デルタパワー経過図：睡眠時間におけるデルタ波の量（パワー量）推移" : "Delta Power Chronogram:change of delta power volume during sleep period"}
        />
        <DeltaPowerTimeChart 
          analyzationResultData={resultData}
          age={age  ?? 0}
          measureStartDate={measureStartDate ?? 0}
          lang={lang ?? 'ja'}
          height={320}
        />
        <MarginUp lang={lang ?? 'ja'}>
          <Headline
            title={lang === 'ja' ?"健康づくりのための睡眠ガイド2023 / 健康づくりのための睡眠指針の改訂に関する検討会" : "A Guide of sleep 2023 for your health / Study Group on Revision of Sleep Guidelines for Health Promotion"}
          />
          <Spacer height={8} />
          <GoodSleepCondition lang={lang ?? 'ja'} small/>
      </MarginUp>
    </Container>
  );


  switch (pageNumber) {
    case 1:
      return renderPage1();
    case 2:
      return renderPage2();
    default:
      return renderPage1();
  }
};

const Container = styled('div')`
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
  box-sizing: border-box;
  padding: 0 20px;
`

const MarginUp = styled("div")<{lang: "en" | "ja"}>`
  margin-top: ${({lang})=> lang === "en"?"-40px": "-40px"};
`